import { useCallback, useRef } from 'react';

/**
 * Hook that creates a throttled version of a callback function.
 * The throttled function will only execute at most once per specified delay period.
 *
 * @param callback - The function to throttle
 * @param delay - The minimum time (in milliseconds) between function executions
 * @returns A throttled version of the callback that will execute at most once per delay period
 *
 * @example
 * const throttledScroll = useThrottle(() => {
 *   console.log('Scrolling...');
 * }, 1000);
 *
 * useEffect(() => {
 *   window.addEventListener('scroll', throttledScroll);
 *   return () => window.removeEventListener('scroll', throttledScroll);
 * }, [throttledScroll]);
 */
export function useThrottle<T extends ( ...args: any[] ) => void>(
  callback: T,
  delay: number
): T {
  const lastCallRef = useRef( 0 );
  const callbackRef = useRef( callback );

  // Keep callback ref up to date
  callbackRef.current = callback;

  return useCallback(
    ( ( ...args: any[] ) => {
      const now = Date.now();
      if ( now - lastCallRef.current >= delay ) {
        lastCallRef.current = now;
        callbackRef.current( ...args );
      }
    } ) as T,
    [ delay ]
  );
}
